<template>
    <div class="radio-group-wrapper">
        <!-- *********************************************
        *                    Top Label                   *
        ********************************************** -->
        <label-generic
        v-if="label"
        class="radio-group-label"
        :class="{ 'error-state': errorState }"
        :multipleLines="labelHasMultipleLines"
        :text="label"
        :mandatory="mandatory"
        :errorState="errorState"/>
        <!-- *********************************************
        *                Radio Container                 *
        ********************************************** -->
        <div class="input-container" :class="{ 'horizontal-direction': horizontalDirection }">
            <!-- Radio Buttons List -->
            <selector-radio
            v-for="radio in radioParams"
            :key="radio.id"
            :id="radio.id"
            :text="radio.text"
            :value="radio.value"
            :disabled="radio.disabled"
            :selected="radio.selected"
            :errorState="radio.errorState || errorState"
            :checkStyle="radio.checkStyle"
            :hasIcon="radio.hasIcon"
            :iconPath="radio.iconPath"
            :tooltipInfo="radio.tooltipInfo"
            :tooltipIcon="radio.tooltipIcon"
            :preventLabelAutoCapitalize="radio.preventLabelAutoCapitalize"
            @clicked="emit('clicked', radio.id)">
                <div v-if="radio.hasSlot && radio.selected" class="slot-wrapper">
                    <slot :name="`extraInfo${radio.id}`"></slot>
                </div>
            </selector-radio>
        </div>
        <!-- *********************************************
        *              Bottom Error Message              *
        ********************************************** -->
        <div v-if="errorMessage && errorState" class='error-message'>{{ errorMessage }}</div>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'
import SelectorRadio from '@/components/generic/selectors/radio/Radio'

/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received
@radioParams { Array } each item is an Object
    - id { String, Number } - radio id
    - text { String } - text
    - value { String } - radio value
    - disabled { Boolean } - component is disabled or not
    - selected { Boolean } - radio is selected or not
    - errorState { Boolean } - error state
    - checkStyle { Boolean } - when the radio is selected, it returns the same icon as used in the checkbox
    - hasIcon { Boolean }
    - iconPath { String } path to the icon
    - tooltipInfo {Object} with:
        - title {String} - tooltip title
        - text {String} - tooltip text
        - description {String} - tooltip description
        - negative {Boolean} - prop to apply negative styles
        - size {String} can be [medium, small, tiny (default)]
        - leftButtonText {String} - text for left button, has default text if this is not passed
        - rightButtonText {String} - text for right button, has default text if this is not passed
        - applyPositionFixed {Boolean} - prop to force tooltipo to use fixed position
@tooltipIcon {String} - icon name to trigger tooltip
@label { String } - text label
@labelHasMultipleLines { Boolean }
@mandatory { Boolean } - adds * to labelText
@errorState { Boolean } - property to show if has error or not
@errorMessage { String } - error message to display
@horizontalDirection { Boolean } - change the direction of the options radio to vertical for horizontal (*the defaut is vertical)
*/
const props = defineProps({
    label: String,
    labelHasMultipleLines: Boolean,
    mandatory: Boolean,
    errorState: Boolean,
    errorMessage: String,
    radioParams: Array,
    horizontalDirection: Boolean
})
</script>

<style lang="scss" scoped>
.radio-group-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .input-container {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 12px;
        row-gap: 16px;

        &.horizontal-direction {
            flex-direction: row;
            gap: 48px;
            row-gap: 16px;
        }
    }

    .error-message {
        @include H7-Regular;
        line-height: 16px;
        color: var(--negative);
    }

    .radio-group-label {
        color: var(--secondary-dark-1);

        &.error-state {
            color: var(--negative);
        }
    }
}
</style>
